import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Text from '@zenbusiness/zui/Text';

import { HomeSection } from '#client/components/HomeSection';
import { ENABLE_STANDALONE_PRODUCT_PAGE } from '#client/constants/experimentsFlags';
import { useFilteredProductCards } from '#client/hooks/useFilteredProductCards';
import { useSearchParam } from '#client/hooks/useSearchParam';

import { ProductCardList } from './components/ProductCardList';
import { ProductCardsFilter } from './components/ProductCardsFilter';
import { SearchEmptyState } from './components/SearchEmptyState';
import {
  DEFAULT_CATEGORY_FILTER,
  PRODUCTS_FILTER_PARAM_KEY,
  PRODUCTS_SEARCH_PARAM_KEY
} from './constants';
import { filterProductsByTitle, applyRedirectUrls } from './helper';
import * as Styled from './ProductCards.styles';

function ProductCards({ title, handleOpenModal }) {
  const [searchParams] = useSearchParams();
  const productCategory = searchParams.get(PRODUCTS_FILTER_PARAM_KEY) || DEFAULT_CATEGORY_FILTER;
  const { searchParamValue: productName, isSearchActive } = useSearchParam(
    PRODUCTS_SEARCH_PARAM_KEY
  );

  const enableRedirectStandalone = useExperimentFlag(ENABLE_STANDALONE_PRODUCT_PAGE, false);

  const { data: products, loading } = useFilteredProductCards([
    productCategory
  ]);
  const filterItems = products?.categories || [];

  const filteredProductCards = applyRedirectUrls(filterProductsByTitle(
    productName,
    products?.cards
  ), enableRedirectStandalone);

  const showLoading = loading && isSearchActive;
  const sectionTitle = isSearchActive ? '' : title;
  const description = isSearchActive && !loading && filteredProductCards.length > 0
    ? `${filteredProductCards.length} ${
      filteredProductCards.length === 1 ? 'result' : 'results'
    } for "${productName}"`
    : '';

  return (
    <HomeSection
      title={(
        <Text color="primary" size="xl" weight="medium">
          {sectionTitle}
        </Text>
      )}
      subtitle={(
        <Text color="secondary" size="sm" weight="regular">
          {description}
        </Text>
      )}
      slots={{
        end: !isSearchActive && (
          <ProductCardsFilter items={filterItems} loading={loading} />
        )
      }}
    >
      {showLoading && (
        <Styled.SpinnerContainer>
          <LoadingSpinner />
        </Styled.SpinnerContainer>
      )}

      <ProductCardList
        loading={!isSearchActive && loading}
        onOpenModal={handleOpenModal}
        productCards={filteredProductCards}
      />
      <SearchEmptyState
        hasResults={filteredProductCards.length > 0}
        isSearchActive={isSearchActive}
        loading={loading}
      />
    </HomeSection>
  );
}

ProductCards.propTypes = {
  title: PropTypes.string.isRequired,
  handleOpenModal: PropTypes.func.isRequired
};

export default ProductCards;
